import { hasEnvelope, pointerTo } from "./envelopePointer";
import { BpPhase, BpService, BpSubservice } from "./types";

// An envelope attribute the author may simply not have set. Defaulting a missing
// override-hours to 0 (or a missing quantity to 1) and writing that back on the
// next save is what froze literal zeros into stored payloads, where they are no
// longer distinguishable from a deliberate zero -- and once one is there, the API
// can no longer fill the catalog's value in its place. Payload values arrive as
// either numbers or numeric strings depending on who wrote the envelope.
const authoredNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Applies only the values the editor actually holds, so an unset row leaves the
// envelope's own value (and the absence of one) untouched.
const withAuthored = (
  attributes: Record<string, any>,
  updates: Record<string, any>
): Record<string, any> => {
  const result = { ...attributes };
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) result[key] = value;
  });
  return result;
};

// The token a relationship pointer names its target by. A pointer carries `id`
// when the target envelope names an existing record and `lid` when the target is
// itself a local envelope (see envelopePointer.ts) -- reading `id` alone treats
// every lid pointer as absent.
export const pointerToken = (relationship: any): string | undefined => {
  const token = relationship?.data?.id ?? relationship?.data?.lid;
  return token === null || token === undefined ? undefined : String(token);
};

// ── Payload parsing ────────────────────────────────────────────────────────────

export const parsePayload = (
  payload: any[],
  serviceType: "professional_services" | "managed_services",
  defaultPhases: BpPhase[]
): { phases: BpPhase[]; services: BpService[] } => {
  const existingPhases = payload.filter((p) => p.type === "project-phases");
  const phases: BpPhase[] =
    serviceType === "professional_services"
      ? existingPhases?.length
        ? existingPhases
            .map((p) => ({
              id: String(p.id ?? p.lid),
              name: p.attributes?.name ?? "",
              position: p.attributes?.position ?? 0,
            }))
            .sort((a, b) => a.position - b.position)
        : defaultPhases
      : [];

  const subMap: Record<string, BpSubservice[]> = {};
  payload
    .filter((p) => p.type === "project-subservices")
    .forEach((p) => {
      // Every sub-item ProjectContent::EnvelopeBuilder generates names its parent
      // by `lid`. Reading `id` alone collapsed all of them under "", where they
      // matched no service and rendered nowhere. Services are keyed the same way
      // (`ps.id ?? ps.lid`), so the two agree.
      const serviceId = pointerToken(p.relationships?.["project-service"]) ?? "";
      if (!subMap[serviceId]) subMap[serviceId] = [];
      subMap[serviceId].push({
        id: String(p.id ?? p.lid),
        name: p.attributes?.name ?? "",
        position: p.attributes?.position ?? 0,
        quantity: authoredNumber(p.attributes?.quantity),
        hours: authoredNumber(p.attributes?.["override-hours"]),
        resourceId: pointerToken(p.relationships?.["project-resource"]) ?? null,
      });
    });
  Object.values(subMap).forEach((arr) =>
    arr.sort((a, b) => a.position - b.position)
  );

  const services: BpService[] = payload
    .filter(
      (p) =>
        p.type === "project-services" &&
        (p.attributes?.["service-type"] ?? "professional_services") ===
          serviceType
    )
    .map((ps) => {
      const phaseId =
        pointerToken(ps.relationships?.["project-phase"]) || phases?.[0]?.id || "";
      return {
        id: String(ps.id ?? ps.lid),
        catalogId:
          ps.relationships?.service?.data?.id != null
            ? String(ps.relationships.service.data.id)
            : null,
        name: ps.attributes?.name ?? "",
        position: ps.attributes?.position ?? 0,
        serviceType,
        phaseId:
          serviceType === "professional_services" ? String(phaseId) : null,
        loadedPhaseId:
          serviceType === "professional_services" ? String(phaseId) : null,
        subservices: subMap[String(ps.id ?? ps.lid)] ?? [],
        quantity: authoredNumber(ps.attributes?.quantity),
        hours: authoredNumber(ps.attributes?.["override-hours"]),
        resourceId: pointerToken(ps.relationships?.["project-resource"]) ?? null,
      };
    })
    .sort((a, b) => a.position - b.position);

  return { phases, services };
};

// ── Payload reconstruction ─────────────────────────────────────────────────────

// Whether the author moved the service. Its pointer in the payload being saved
// says where it stands, so a move back after a save (A to B, save, B to A)
// still counts while the tab stays open. With no pointer, it stands where
// parsePayload showed it.
const wasMoved = (
  relationships: Record<string, any> | undefined,
  upd: BpService
): boolean =>
  upd.phaseId !==
  (pointerToken(relationships?.["project-phase"]) || upd.loadedPhaseId);

// A service moved to a phase with no envelope in the payload (with none, the
// editor offers the account's catalog phases) needs one to point at. Same
// shape as handleAdd's, keyed by the catalog phase id as New.tsx keys them.
const phaseEnvelopesFor = (
  rawPayload: any[],
  serviceUpdates: Map<string, BpService>,
  phases: BpPhase[]
): any[] => {
  const added: any[] = [];
  rawPayload.forEach((item) => {
    const s =
      item.type === "project-services"
        ? serviceUpdates.get(String(item.id ?? item.lid))
        : undefined;
    if (!s || s.phaseId === null || !wasMoved(item.relationships, s)) return;
    if (hasEnvelope([...rawPayload, ...added], "project-phases", s.phaseId)) {
      return;
    }
    const phase = phases.find((p) => p.id === s.phaseId);
    if (!phase) return;
    added.push({
      type: "project-phases",
      lid: phase.id,
      attributes: { name: phase.name, position: phase.position },
      relationships: {
        phase: { data: { type: "phases", id: Number(phase.id) } },
      },
    });
  });
  return added;
};

// The apply skips a service whose phase pointer names no envelope in the
// payload ("missing remapped project-phases"), and with no pointer the API
// gives it its catalog task's phase. So a pointer is written only when the
// service was moved. An unmoved one keeps what it had: parsePayload shows a
// pointerless service under the first phase, and writing that back would
// override its catalog phase. A pointer that resolves to nothing is dropped.
const withPhase = (
  relationships: Record<string, any>,
  upd: BpService,
  envelopes: any[]
): Record<string, any> => {
  if (
    wasMoved(relationships, upd) &&
    hasEnvelope(envelopes, "project-phases", upd.phaseId)
  ) {
    return {
      ...relationships,
      "project-phase": pointerTo(envelopes, "project-phases", upd.phaseId),
    };
  }
  const loaded = pointerToken(relationships["project-phase"]);
  if (
    loaded === undefined ||
    hasEnvelope(envelopes, "project-phases", loaded)
  ) {
    return relationships;
  }
  const result = { ...relationships };
  delete result["project-phase"];
  return result;
};

export const buildUpdatedPayload = (
  rawPayload: any[],
  serviceType: "professional_services" | "managed_services",
  services: BpService[],
  phases: BpPhase[] = []
): any[] => {
  const serviceUpdates = new Map(services.map((s) => [s.id, s]));
  const subserviceUpdates = new Map(
    services.flatMap((s) => s.subservices.map((sub) => [sub.id, sub]))
  );
  const addedPhases =
    serviceType === "professional_services"
      ? phaseEnvelopesFor(rawPayload, serviceUpdates, phases)
      : [];
  const envelopes = [...rawPayload, ...addedPhases];

  const updatedPayload = rawPayload.map((item) => {
    const id = String(item.id ?? item.lid);

    if (
      item.type === "project-services" &&
      (item.attributes?.["service-type"] ?? "professional_services") ===
        serviceType
    ) {
      const upd = serviceUpdates.get(id);
      if (!upd) return item;
      const updated: any = {
        ...item,
        attributes: withAuthored(item.attributes ?? {}, {
          position: upd.position,
          quantity: upd.quantity,
          "override-hours": upd.hours,
        }),
        relationships: {
          ...(serviceType === "professional_services"
            ? withPhase(item.relationships ?? {}, upd, envelopes)
            : item.relationships),
          "project-resource": pointerTo(
            rawPayload,
            "project-resources",
            upd.resourceId
          ),
        },
      };
      return updated;
    }

    if (item.type === "project-subservices") {
      const upd = subserviceUpdates.get(id);
      if (!upd) return item;
      return {
        ...item,
        attributes: withAuthored(item.attributes ?? {}, {
          quantity: upd.quantity,
          "override-hours": upd.hours,
        }),
        relationships: {
          ...item.relationships,
          "project-resource": pointerTo(
            rawPayload,
            "project-resources",
            upd.resourceId
          ),
        },
      };
    }

    return item;
  });
  return [...updatedPayload, ...addedPhases];
};
